import {
  faceCode,
  faceNotation,
  isImmune,
  squareName,
  type Card,
  type Face,
  type MatchEvent,
  type MatchView,
  type PassKind,
  type Pawn,
  type PatternName,
  type RecordedLink,
  type ScoreBreakdown,
  type Side,
  type Square,
} from '../engine';
import { t, type MessageKey } from '../i18n';
import type { Controller } from './matchHost';

/** Text for events, pawns and positions: the log (§19.12) and the text view of all state (§19.13). */

export interface Names {
  pawn(id: string): string;
  side(side: Side): string;
  /** True for a campaign reserve: a roster pawn off the board that can pay for Wilds. */
  isReserve(id: string): boolean;
}

/**
 * A pawn's name for people: its own name in the campaign, else its side and the file it
 * started on ("North pawn F"), never an internal id.
 */
function defaultPawnName(id: string, side: Side | undefined): string {
  const [prefix, file] = id.split('-');
  const s = side ?? (prefix === 'S' || prefix === 'N' ? prefix : undefined);
  return s && file && /^[A-F]$/.test(file) ? t('pawn.default', { side: t(`side.${s}`), file }) : id;
}

export function namesFor(v: MatchView, controllers?: Readonly<Record<Side, Controller>>): Names {
  return {
    pawn: (id) => {
      const p = v.pawns[id];
      if (p?.name) return p.name;
      const r = v.reservePawns[id];
      if (r?.name) return r.name;
      if (p?.standIn || r?.standIn)
        return t('pawn.standInNamed', { n: id.replace(/^\D+-?/, '') || id });
      return defaultPawnName(id, p?.side);
    },
    side: (side) => {
      const c = controllers?.[side];
      return c ? `${c.name} (${t(`side.${side}`)})` : t(`side.${side}`);
    },
    isReserve: (id) => !v.pawns[id] && !!v.reservePawns[id],
  };
}

/**
 * A direction from a side's point of view: "forward", "back-left"... South faces up the board
 * (toward rank 6, files A to F from left to right); North faces the other way.
 */
function directionWords(dx: number, dy: number, side: Side): string {
  const facing = side === 'S' ? 1 : -1;
  const ahead = Math.sign(dy) * facing;
  const across = Math.sign(dx) * facing;
  const v = ahead > 0 ? t('dir.forward') : ahead < 0 ? t('dir.back') : '';
  const h = across > 0 ? t('dir.right') : across < 0 ? t('dir.left') : '';
  return v && h ? t('dir.diagonal', { v, h }) : v || h;
}

/** One link of a recorded route in words, as its owner would replay it: "2 forward-left". */
export function recordedMoveWords(link: RecordedLink, owner: Side): string {
  const m = link.move;
  switch (m.type) {
    case 'slide': {
      const n = Math.max(Math.abs(m.dx), Math.abs(m.dy));
      const dir = directionWords(m.dx, m.dy, owner);
      return n === 1 ? t('route.slide.one', { dir }) : t('route.slide', { n, dir });
    }
    case 'knight':
      return t('route.knight', { dir: directionWords(m.dx, m.dy, owner) });
    case 'hops': {
      const first = m.dirs[0];
      return m.dirs.length === 1 && first
        ? t('route.hop', { dir: directionWords(first.dx, first.dy, owner) })
        : t('route.hops', { n: m.dirs.length });
    }
    case 'knights': {
      const first = m.jumps[0];
      return m.jumps.length === 1 && first
        ? t('route.knight', { dir: directionWords(first.dx, first.dy, owner) })
        : t('route.knights', { n: m.jumps.length });
    }
    case 'still':
      return t('route.still');
  }
}

/** A recorded route in words: "Red 2, 2 squares forward; then Blue 2, 1 square forward-left". */
export function routeWords(links: readonly RecordedLink[], owner: Side): string {
  return links
    .map((l) =>
      t('route.step', {
        face: l.wasWild ? t('card.wildNamed', { face: faceLong(l.face) }) : faceLong(l.face),
        move: recordedMoveWords(l, owner),
      }),
    )
    .join(t('route.then'));
}

export const faceLong = (f: Face): string =>
  t('card.label', { color: t(`color.${f.color}`), number: f.number });

export const cardShort = (c: Card | null | undefined): string =>
  !c ? '?' : c.kind === 'wild' ? 'W' : faceCode(c.face as Face);

export const cardLong = (c: Card | null | undefined): string =>
  !c ? '?' : c.kind === 'wild' ? t('card.wild') : faceLong(c.face as Face);

/** The name of any Long Night thing by id: a Superstition, Curio, Stamp, Carving… */
export const itemName = (id: string): string => {
  const key = `item.${id}` as MessageKey;
  const v = t(key);
  return v === key ? id : v;
};
/** What a Long Night thing does ("Each Red link: +3 P."), or '' when there is nothing to say. */
export const itemText = (id: string, params?: Record<string, string | number>): string => {
  const key = `item.${id}.text` as MessageKey;
  const v = t(key, params);
  return v === key ? '' : v;
};
/** A status effect's name ("Warded"). */
export const statusName = (id: string): string => itemName(`status.${id}`);
/** An Omen's name ("Candle"); a Shrouded one is "a dark square". */
export const omenName = (id: string): string => itemName(`omen.${id}`);
/** What earned or cost brass: an item, an Omen, a House Rule. */
export const reasonName = (id: string): string => {
  for (const key of [`item.${id}`, `item.omen.${id}`, `item.rule.${id}`, `item.reason.${id}`]) {
    const v = t(key as MessageKey);
    if (v !== key) return v;
  }
  return id;
};

export const patternName = (p: PatternName | null): string =>
  p ? t(`preview.pattern.${p}`) : t('preview.pattern.none');

function scoreExtra(b: ScoreBreakdown): string {
  let extra = '';
  if (b.multiplier.pattern)
    extra += t('log.extra.pattern', { pattern: patternName(b.multiplier.pattern) });
  if (b.multiplier.fullCircle) extra += t('log.extra.fullCircle');
  return extra;
}

/** One log line for an event, or null for events the log skips. */
export function formatEvent(e: MatchEvent, n: Names): string | null {
  switch (e.type) {
    case 'MatchStarted':
      return t('log.matchStarted', { version: e.rulesVersion, preset: e.preset });
    case 'RoundStarted':
      return e.scores.N > 0
        ? t('log.roundStartedHead', { round: e.round, n: e.scores.N })
        : t('log.roundStarted', { round: e.round });
    case 'StarterChosen':
      return t(`log.starter.${e.how}`, {
        side: n.side(e.starter),
        round: e.round,
        chooser: e.chooser ? n.side(e.chooser) : '',
      });
    case 'TurnStarted':
      return null;
    case 'Passed':
      return e.kind === 'outOfTurns' && e.limit
        ? t('log.passed.outOfTurnsRule', {
            side: n.side(e.side),
            n: e.limit.n,
            rule: itemName(`rule.${e.limit.rule}`),
          })
        : t(`log.passed.${e.kind}`, { side: n.side(e.side) });
    case 'CardDrawn':
      if (e.reason === 'setup') return null;
      return e.card
        ? t('log.drawn', { side: n.side(e.side), card: cardShort(e.card) })
        : t('log.drawnHidden', { side: n.side(e.side) });
    case 'LinkPlayed':
      return e.collateralPawnId
        ? t(n.isReserve(e.collateralPawnId) ? 'log.linkReserve' : 'log.linkCollateral', {
            side: n.side(e.side),
            notation: e.step.notation,
            pawn: n.pawn(e.collateralPawnId),
          })
        : t('log.link', { side: n.side(e.side), notation: e.step.notation });
    case 'PawnPawned':
      return null;
    case 'PawnCaptured':
      return t(e.epitaphId ? 'log.capturedEpitaph' : 'log.captured', {
        pawn: n.pawn(e.pawnId),
        square: squareName(e.square),
      });
    case 'PawnCrowned':
      return t('log.crowned', { pawn: n.pawn(e.pawnId), square: squareName(e.square) });
    case 'ChainScored':
      return t(e.source === 'epitaph' ? 'log.scoredEpitaph' : 'log.scored', {
        side: n.side(e.side),
        p: e.breakdown.points.total,
        m: e.breakdown.multiplier.total,
        score: e.breakdown.score,
        extra: scoreExtra(e.breakdown),
      });
    case 'InscriptionSet':
      if (e.inscription.veiled) return t('log.inscription.veiled', { pawn: n.pawn(e.pawnId) });
      return t(`log.inscription.${e.how}`, {
        pawn: n.pawn(e.pawnId),
        route: routeWords(e.inscription.links, e.side),
      });
    case 'EpitaphReceived':
      if (e.epitaph.inscription.veiled)
        return t('log.epitaphReceivedVeiled', { side: n.side(e.side) });
      return t('log.epitaphReceived', {
        side: n.side(e.side),
        route: routeWords(e.epitaph.inscription.links, e.side),
      });
    case 'EpitaphDiscarded':
      if (e.epitaph.inscription.veiled)
        return t('log.epitaphDiscardedVeiled', { side: n.side(e.side) });
      return t('log.epitaphDiscarded', {
        side: n.side(e.side),
        route: routeWords(e.epitaph.inscription.links, e.side),
      });
    case 'EpitaphPlayed':
      return t('log.epitaphPlayed', {
        side: n.side(e.side),
        pawn: n.pawn(e.pawnId),
        route: e.route.map((r) => r.notation).join(', '),
      });
    case 'Recovered':
      return t('log.recovered', { side: n.side(e.side), card: cardShort(e.card) });
    case 'CrownBonusCreated':
      return t('log.crownPending', { n: e.amount, pawn: n.pawn(e.pawnId) });
    case 'CrownBonusResolved':
      return e.outcome === 'awarded'
        ? t('log.crownAwarded', { side: n.side(e.side), n: e.amount, pawn: n.pawn(e.pawnId) })
        : t('log.crownDiscarded', { pawn: n.pawn(e.pawnId) });
    case 'RoundEnded':
      return e.winner
        ? t('log.roundEnded', {
            round: e.round,
            s: e.scores.S,
            n: e.scores.N,
            winner: n.side(e.winner),
          })
        : t('log.roundTied', { round: e.round, s: e.scores.S, n: e.scores.N });
    case 'MatchEnded':
      return e.result.winner
        ? t('log.matchEnded', { winner: n.side(e.result.winner) })
        : t('log.matchDrawn');
    case 'Conceded':
      return t('log.conceded', { side: n.side(e.side) });
    // Long Night events (LEDGER.MD).
    case 'BrassChanged':
      return t(e.amount >= 0 ? 'log.brassGained' : 'log.brassLost', {
        side: n.side(e.side),
        n: Math.abs(e.amount),
        reason: reasonName(e.reason),
      });
    case 'CurioUsed':
      return t('log.curioUsed', { side: n.side(e.side), curio: itemName(e.curio) });
    case 'TrickPlayed':
      return t('log.trick', {
        side: n.side(e.side),
        trick: itemName(`trick.${e.trick}`),
        on: e.target.pawnId
          ? t('log.trickOn', { pawn: n.pawn(e.target.pawnId) })
          : e.target.face
            ? t('log.trickOn', { pawn: faceLong(e.target.face) })
            : '',
      });
    case 'StatusChanged':
      return e.change === 'added'
        ? t('log.statusAdded', { pawn: n.pawn(e.pawnId), status: statusName(e.status) })
        : null;
    case 'OmenTriggered':
      return t('log.omen', {
        pawn: n.pawn(e.pawnId),
        omen: omenName(e.kind),
        square: squareName(e.square),
      });
    case 'OmenAppeared':
      return t('log.omenAppeared', { omen: omenName(e.kind), square: squareName(e.square) });
    case 'CardBurned':
      return t('log.burned', { side: n.side(e.side), card: cardShort(e.card) });
    case 'PawnFell':
      return t(e.cause === 'trapdoor' ? 'log.fellTrapdoor' : 'log.fellSmoldering', {
        pawn: n.pawn(e.pawnId),
        square: squareName(e.square),
      });
    case 'PawnMoved':
      return t(`log.moved.${e.cause}`, { pawn: n.pawn(e.pawnId), square: squareName(e.to) });
    case 'Horror':
      // Lights Out keeps what moved in the dark out of the log line; the board shows it.
      return t(`log.horror.${e.horror}`, {
        side: n.side(e.side),
        pawn: e.pawnId ? n.pawn(e.pawnId) : e.moves[0] ? n.pawn(e.moves[0].pawnId) : '',
        square: e.at ? squareName(e.at) : e.moves[0] ? squareName(e.moves[0].to) : '',
        n: e.moves.length,
        half: t(`log.horror.half.${e.dark ?? 'left'}`),
      });
    case 'ItemMoved':
      return e.reason === 'grew'
        ? null
        : t(`log.item.${e.reason}`, {
            item: itemName(e.id),
            side: e.from ? n.side(e.from) : '',
            to: e.to ? n.side(e.to) : '',
          });
    case 'CardChanged':
      return e.change === 'returned'
        ? null
        : e.change === 'stamped' && e.stamp
          ? t('log.cardStamped', { side: n.side(e.side), stamp: itemName(e.stamp) })
          : t('log.cardShattered', { side: n.side(e.side) });
  }
}

/** "the pawn on B3", or the pawn's name in the campaign. */
export function pawnRef(v: MatchView, id: string, n: Names): string {
  const p = v.pawns[id];
  if (p?.name) return p.name;
  if (p?.square) return t('pawn.onSquare', { square: squareName(p.square) });
  return n.pawn(id);
}

/** Accessible description of one pawn. */
export function describePawn(v: MatchView, p: Pawn, n: Names, marked = false): string {
  const name = n.pawn(p.id);
  const parts = [p.name || p.standIn ? t('pawn.label', { side: t(`side.${p.side}`), name }) : name];
  if (p.crowned) parts.push(t('pawn.crowned'));
  if (isImmune(v, p)) parts.push(t('pawn.immune'));
  if (p.inscription)
    parts.push(
      t('pawn.inscribed', {
        route: routeWords(p.inscription.links, p.side),
        score: p.inscription.recordedScore,
      }),
    );
  if (p.former) parts.push(t('pawn.former'));
  if (p.standIn) parts.push(t('pawn.standIn'));
  if (marked) parts.push(t('pawn.marked'));
  // The Long Night: its statuses and Carvings (drawn as marks on the piece).
  for (const st of v.ledger?.statuses[p.id] ?? []) parts.push(statusName(st.kind));
  if (p.carvings?.length)
    parts.push(t('pawn.carved', { list: p.carvings.map((c) => itemName(c.id)).join(', ') }));
  return parts.join(', ');
}

/** The whole public position as text (§19.13: all state is readable as text). */
export function describePosition(v: MatchView, n: Names): string[] {
  const lines: string[] = [];
  lines.push(
    `${t('match.round', { round: v.round, max: v.config.constants.MAX_ROUNDS })}. ${t('match.preset', { preset: v.config.preset })}. ${t('match.starter', { side: n.side(v.roundStarter) })}.`,
  );
  for (const side of ['S', 'N'] as const) {
    const pl = v.players[side];
    const pawns = Object.values(v.pawns)
      .filter((p) => p.side === side && p.status === 'onBoard' && p.square)
      .map((p) => `${squareName(p.square!)} ${describePawn(v, p, n)}`);
    lines.push(
      `${n.side(side)}: ${t('match.roundScore')} ${pl.roundScore}, ${t('match.total')} ${pl.totalScore}, ${t('match.roundWins')} ${pl.roundWins}. ${t('match.handDeck', { hand: pl.handCount, deck: pl.deckCount })}. ${t('match.spent')} ${pl.spent.length}, ${t('match.removed')} ${pl.removed.length}.${pl.passed ? ` ${t('match.passed')}.` : ''}${pl.epitaphPlays > 0 ? ` ${t('match.epitaphPlayed')}.` : ''}`,
    );
    lines.push(pawns.length ? pawns.join('; ') : '—');
    if (pl.epitaphs.length)
      lines.push(
        `${t('epitaph.slot')}: ${pl.epitaphs.map((e) => (e.inscription.veiled ? t('epitaph.veiled') : routeWords(e.inscription.links, side))).join(' / ')}`,
      );
  }
  const top = v.pile[v.pile.length - 1];
  lines.push(
    top
      ? `${t('match.top')}: ${top.source === 'epitaph' ? t('pile.epitaphTop', { face: faceCode(top.face) }) : top.source === 'chalk' ? t('pile.chalkTop', { face: faceCode(top.face) }) : faceNotation(top.face, top.wasWild)}`
      : t('match.pileEmpty'),
  );
  return lines;
}

/** What a player did on a turn, to follow the other player's turns (§19.12). */
export interface TurnSummary {
  readonly side: Side;
  /** Each link, or each step of a replayed Epitaph, in notation. */
  readonly moves: readonly string[];
  /** Where the moving pawn went: its starting square, then every landing. */
  readonly route: readonly Square[];
  readonly epitaph: boolean;
  readonly score: ScoreBreakdown | null;
  readonly captures: number;
  readonly crowned: boolean;
  readonly recovered: Card | null;
  readonly passed: PassKind | null;
}

/**
 * The latest turn `side` has taken (or is taking) this round, from the log, or null when it has
 * not acted yet this round. A log that starts in the middle of a round (a match set up from a
 * position, as in the tutorial) starts in a turn: one the side may have acted in.
 */
export function lastTurnOf(log: readonly MatchEvent[], side: Side): TurnSummary | null {
  let start: number | null = null;
  for (let i = log.length - 1; i >= 0; i--) {
    const e = log[i] as MatchEvent;
    if (e.type === 'RoundStarted') break;
    if (e.type === 'TurnStarted' && e.side === side) {
      start = i;
      break;
    }
    if (i === 0) start = -1;
  }
  if (start === null) return null;
  const moves: string[] = [];
  const route: Square[] = [];
  let epitaph = false;
  let score: ScoreBreakdown | null = null;
  let captures = 0;
  let crowned = false;
  let recovered: Card | null = null;
  let passed: PassKind | null = null;
  const walk = (from: Square, landings: readonly Square[]) => {
    // A link that starts somewhere else than the last one ended (through a Looking Glass) shows
    // the jump.
    const last = route[route.length - 1];
    if (!last || last.x !== from.x || last.y !== from.y) route.push(from);
    route.push(...landings);
  };
  for (let i = start + 1; i < log.length; i++) {
    const e = log[i] as MatchEvent;
    if (e.type === 'TurnStarted' || e.type === 'RoundEnded') break;
    if (e.type === 'LinkPlayed' && e.side === side) {
      moves.push(e.step.notation);
      walk(e.step.from, e.step.landings);
    } else if (e.type === 'EpitaphPlayed' && e.side === side) {
      epitaph = true;
      for (const r of e.route) {
        moves.push(r.notation);
        walk(r.from, r.landings);
      }
    } else if (e.type === 'ChainScored' && e.side === side) score = e.breakdown;
    else if (e.type === 'PawnCaptured' && e.side !== side) captures++;
    else if (e.type === 'PawnCrowned' && e.side === side) crowned = true;
    else if (e.type === 'Recovered' && e.side === side) recovered = e.card;
    else if (e.type === 'Passed' && e.side === side) passed = e.kind;
  }
  return { side, moves, route, epitaph, score, captures, crowned, recovered, passed };
}

/** A turn in one sentence, or null while nothing has happened in it yet. */
export function describeTurn(turn: TurnSummary, name: string): string | null {
  if (turn.passed) return t(`lastTurn.passed.${turn.passed}`, { name });
  if (turn.recovered) return t('lastTurn.recovered', { name, card: cardShort(turn.recovered) });
  if (turn.moves.length === 0) return null;
  const parts = [
    t(turn.epitaph ? 'lastTurn.epitaph' : 'lastTurn.links', {
      name,
      moves: turn.moves.join(', '),
    }),
  ];
  if (turn.score)
    parts.push(
      t('lastTurn.score', {
        p: turn.score.points.total,
        m: turn.score.multiplier.total,
        score: turn.score.score,
      }),
    );
  if (turn.captures > 0)
    parts.push(
      turn.captures === 1 ? t('lastTurn.capture') : t('lastTurn.captures', { n: turn.captures }),
    );
  if (turn.crowned) parts.push(t('lastTurn.crowned'));
  return parts.join(' · ');
}
